import { useMemo } from "react";
import { Task } from "@common/types/task";
import { AccountsTableColumn } from "@common/types/views/accountsView";
import { Loader } from "@design-system/Loader/Loader";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { useEntitiesContexts } from "@hooks/useEntitiesContexts";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import {
    accountsView,
    accountsViewConfig,
} from "@components/accounts/views/accountsViewConfig";
import { AccountsTableRow } from "@components/accounts/views/accountsView.types";
import {
    hasMatchingConnectionId,
    hasMatchingIntegrationAccount,
} from "@helpers/financialAccount";
import { TaskBodyCommandIsland } from "../TaskBodyCommandIsland";
import taskBodyTableStyles from "../TaskBodyTable.module.scss";
import { getConnectionMetadata } from "../TaskBody.lib";

interface BrokenConnectionTaskBodyProps {
    selectedTask: Task;
}

const brokenConnectionColumns = [
    { id: AccountsTableColumn.ENTITY_OR_ACCOUNT },
    { id: AccountsTableColumn.BALANCE },
    { id: AccountsTableColumn.STATUS, options: { pin: "right" as const } },
];

const brokenConnectionView = {
    ...accountsView,
    columns: brokenConnectionColumns,
};

const brokenConnectionViewConfig = {
    ...accountsViewConfig,
    nesting: undefined,
};

export const BrokenConnectionTaskBody = ({
    selectedTask,
}: BrokenConnectionTaskBodyProps) => {
    const { connectionId, isIntegrationConnectionId } =
        getConnectionMetadata(selectedTask);

    const entitiesContext = useEntitiesContexts();
    const { isFetched: membersFetched } = useCurrentWorkspaceMembers();

    const tableData = useMemo(() => {
        if (!connectionId) {
            return [];
        }

        return (entitiesContext ?? []).flatMap((entityContext) =>
            entityContext.financialAccounts
                .filter((financialAccount) =>
                    isIntegrationConnectionId
                        ? hasMatchingIntegrationAccount({
                              financialAccount,
                              connectionId,
                          })
                        : hasMatchingConnectionId({
                              financialAccount,
                              connectionId,
                          }),
                )
                .map((financialAccount): AccountsTableRow => ({
                    id: `account-${financialAccount.id}`,
                    entity: entityContext.entity,
                    account: financialAccount,
                    assignedMembers: [],
                    accounts: [],
                })),
        );
    }, [connectionId, entitiesContext, isIntegrationConnectionId]);

    if (!membersFetched || entitiesContext === undefined) {
        return <Loader />;
    }

    return (
        <>
            <ViewTable
                data={tableData}
                view={brokenConnectionView}
                config={brokenConnectionViewConfig}
                className={taskBodyTableStyles.viewTableWithoutTopBorder}
                rowBorders
                enableColumnResizing={false}
                fixedTopOffsetPx
                // The app header hides via a `body` class when fullscreen,
                // which only shifts this table's position, not its own size —
                // the auto top-offset measurement's ResizeObserver alone
                // wouldn't catch it, so force a re-measure on the flag itself.
                emptyView={() => (
                    <span>No accounts found for this connection.</span>
                )}
                footer={<TaskBodyCommandIsland selectedTask={selectedTask} />}
            />
        </>
    );
};
